import {
  type OperationResponse,
  type Parameters,
  type UnknownParamsObject,
} from "../../common.ts";
import { omitPathParameters, replacePathParameters } from "../../utils.ts";
import type { AxiosInstance, AxiosRequestConfig } from "axios";

import type * as Paths from "./paths";

interface OperationMethods {
  "getDiscountItems"(
    parameters?: Parameters<Paths.GetDiscountItems.QueryParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetDiscountItems.Responses.$200>;

  "createDiscountItem"(
    parameters?: Parameters<UnknownParamsObject> | null,
    data?: Paths.CreateDiscountItem.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.CreateDiscountItem.Responses.$200>;

  "getDiscountItem"(
    parameters?: Parameters<
      Paths.GetDiscountItem.QueryParameters &
        Paths.GetDiscountItem.PathParameters
    > | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetDiscountItem.Responses.$200>;

  "updateDiscountItem"(
    parameters?: Parameters<Paths.UpdateDiscountItem.PathParameters> | null,
    data?: Paths.UpdateDiscountItem.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.UpdateDiscountItem.Responses.$200>;

  "deleteDiscountItem"(
    parameters?: Parameters<Paths.DeleteDiscountItem.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.DeleteDiscountItem.Responses.$204>;
}

export const getDiscountItemOperations = (client: AxiosInstance) => {
  const getDiscountItems: OperationMethods["getDiscountItems"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/discount-item";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const createDiscountItem: OperationMethods["createDiscountItem"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/discount-item";
    return client.post(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };
  const getDiscountItem: OperationMethods["getDiscountItem"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/discount-item/{discountItem}";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const updateDiscountItem: OperationMethods["updateDiscountItem"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/discount-item/{discountItem}";
    return client.put(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };
  const deleteDiscountItem: OperationMethods["deleteDiscountItem"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/discount-item/{discountItem}";
    return client.delete(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };

  return {
    getDiscountItems: getDiscountItems,
    createDiscountItem: createDiscountItem,
    getDiscountItem: getDiscountItem,
    updateDiscountItem: updateDiscountItem,
    deleteDiscountItem: deleteDiscountItem,
  };
};
